import React, { useRef, useState } from "react";
import {
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { Lock, AlertCircle, Loader2 } from "lucide-react";

export type CardFunding = "credit" | "debit" | "prepaid" | "unknown";

export interface RejectedCardInfo {
  funding: CardFunding;
  paymentMethodId: string;
}

interface CheckoutPaymentElementFormProps {
  returnUrl: string;
  onSuccess?: () => void;
  onError: (errorMessage: string) => void;
  requireCreditCard?: boolean;
  blockOnNonCredit?: boolean;
  clientSecret?: string | null;
  onCardRejected?: (info: RejectedCardInfo) => void;
  onPaymentMethodValidated?: (paymentMethodId: string) => void;
  processingMessage?: string;
  className?: string;
  submitButtonText?: string;
  externallyDisabled?: boolean;
}

// Checkout-specific variant of ValidatingPaymentElementForm.
// Adds guards against re-submitting a PaymentIntent that already reached a
// terminal state — the browser can re-mount this form (e.g. after a 3DS
// redirect that lands back on /embed/checkout instead of /finalize), and
// Stripe would reject the second `confirmPayment` with
// `payment_intent_unexpected_state`.
//
// /quote-review and other flows keep using the original
// ValidatingPaymentElementForm without these guards.
export const CheckoutPaymentElementForm: React.FC<
  CheckoutPaymentElementFormProps
> = ({
  returnUrl,
  onSuccess,
  onError,
  requireCreditCard = false,
  blockOnNonCredit = true,
  clientSecret,
  onCardRejected,
  onPaymentMethodValidated,
  processingMessage = "Processing Payment...",
  className = "",
  submitButtonText = "Complete Reservation",
  externallyDisabled = false,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);
  const validatedPaymentMethodId = useRef<string | null>(null);

  const hasConfirmedRef = useRef(false);
  const [hasConfirmed, setHasConfirmed] = useState(false);
  const markConfirmed = () => {
    hasConfirmedRef.current = true;
    setHasConfirmed(true);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) return;
    if (hasConfirmedRef.current || isProcessing) return;

    setIsProcessing(true);
    setErrorMessage(null);

    if (clientSecret) {
      try {
        const { paymentIntent } =
          await stripe.retrievePaymentIntent(clientSecret);
        if (
          paymentIntent &&
          (paymentIntent.status === "succeeded" ||
            paymentIntent.status === "processing" ||
            paymentIntent.status === "requires_capture")
        ) {
          markConfirmed();
          setIsProcessing(false);
          onSuccess?.();
          return;
        }
      } catch (err) {
        console.warn(
          "[CheckoutPaymentElementForm] Could not retrieve PaymentIntent",
          err,
        );
      }
    }

    try {
      if (requireCreditCard) {
        if (!clientSecret) {
          const msg = "Missing client secret for confirmation.";
          setErrorMessage(msg);
          onError(msg);
          setIsProcessing(false);
          return;
        }

        const submitResult = await elements.submit();
        if (submitResult.error) {
          const msg =
            submitResult.error.message || "Please complete your card details.";
          setErrorMessage(msg);
          onError(msg);
          setIsProcessing(false);
          return;
        }

        const { error: pmError, paymentMethod } =
          await stripe.createPaymentMethod({ elements });

        if (pmError || !paymentMethod) {
          const msg = pmError?.message || "Could not validate the card.";
          setErrorMessage(msg);
          onError(msg);
          setIsProcessing(false);
          return;
        }

        const funding: CardFunding =
          paymentMethod.type === "card"
            ? ((paymentMethod.card?.funding || "unknown") as CardFunding)
            : "unknown";

        if (funding !== "credit" && blockOnNonCredit) {
          onCardRejected?.({ funding, paymentMethodId: paymentMethod.id });
          setIsProcessing(false);
          return;
        }

        validatedPaymentMethodId.current = paymentMethod.id;

        const { error } = await stripe.confirmPayment({
          clientSecret,
          confirmParams: {
            return_url: returnUrl,
            payment_method: paymentMethod.id,
          },
          redirect: "if_required",
        });

        if (error) {
          const msg = error.message || "An unexpected error occurred.";
          setErrorMessage(msg);
          onError(msg);
        } else {
          markConfirmed();
          onPaymentMethodValidated?.(paymentMethod.id);
          onSuccess?.();
        }
      } else {
        const { error } = await stripe.confirmPayment({
          elements,
          confirmParams: { return_url: returnUrl },
          redirect: "if_required",
        });

        if (error) {
          const msg = error.message || "An unexpected error occurred.";
          setErrorMessage(msg);
          onError(msg);
        } else {
          markConfirmed();
          if (onSuccess) onSuccess();
        }
      }
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : "An unexpected error occurred.";
      setErrorMessage(msg);
      onError(msg);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReady = () => setIsReady(true);

  return (
    <div
      className={`bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden ${className}`}
    >
      <div className="p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="relative min-h-[100px]">
            {!isReady && (
              <div className="absolute inset-0 flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
              </div>
            )}
            <PaymentElement
              onReady={handleReady}
              options={{ layout: "tabs" }}
            />
          </div>

          {errorMessage && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
              <AlertCircle className="h-5 w-5 text-red-500 mr-3 mt-0.5" />
              <p className="text-sm text-red-600 font-medium">{errorMessage}</p>
            </div>
          )}

          <div className="bg-green-50 rounded-lg p-4 border border-green-200">
            <div className="flex items-start">
              <Lock className="h-5 w-5 text-green-600 mr-3 mt-0.5" />
              <div>
                <p className="text-sm text-green-800 font-medium">
                  Encrypted & Secure
                </p>
                <p className="text-xs text-green-600 mt-1">
                  Your payment information is encrypted and processed securely
                  by Stripe.
                </p>
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={
              !stripe ||
              !elements ||
              isProcessing ||
              !isReady ||
              externallyDisabled ||
              hasConfirmed
            }
            className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white py-4 px-6 rounded-lg font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none transform hover:scale-[1.02] flex items-center justify-center"
          >
            {isProcessing ? (
              <>
                <Loader2 className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full mr-3" />
                {processingMessage}
              </>
            ) : (
              <>
                <Lock className="h-5 w-5 mr-2" />
                {submitButtonText}
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};
